/**
 * src/components/shared/EntityMicButton.jsx — Inline mic button for entity headers
 *
 * Tap: start/stop short voice note (useEntityMic)
 * Long-press (>1s): chunked recording for long notes (useChunkedRecorder)
 *
 * Event model: onPointerUp drives ALL actions (start, stop, long-press cancel).
 * onClick is intentionally NOT used — mobile browsers inconsistently fire click
 * after pointerdown/pointerup sequences, especially when button content changes
 * between taps (Mic icon → Square icon).
 */

import { useRef, useCallback, useState, useEffect } from 'react';
import { Mic, Square, Loader, Compass } from 'lucide-react';
import useEntityMic from '../../hooks/useEntityMic';
import useChunkedRecorder from '../../hooks/useChunkedRecorder';

const LONG_PRESS_MS = 1000;

const PROFILE_LABELS = {
  project: 'Note',
  survey: 'Survey',
  production: 'Shop',
  crm: 'CRM',
};

function formatElapsed(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function EntityMicButton({ contextType, contextId, contextHint, profiles }) {
  const [selectedProfile, setSelectedProfile] = useState(null);
  // Override contextHint when a profile is explicitly selected
  const effectiveHint = selectedProfile || contextHint;
  const shortMic = useEntityMic({ contextType, contextId, contextHint: effectiveHint });
  const chunked = useChunkedRecorder({ contextType, contextId, contextHint: effectiveHint });
  const [mode, setMode] = useState(null); // null | 'short' | 'chunked'
  const pressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  const modeRef = useRef(null); // mirror of mode state for use in event handlers

  // Keep modeRef in sync
  useEffect(() => { modeRef.current = mode; }, [mode]);

  // Determine active state from whichever mode is active
  const activeState = mode === 'chunked' ? chunked.state : shortMic.micState;
  const isRecording = activeState === 'recording';
  const isProcessing = activeState === 'processing' || activeState === 'uploading';
  const isDone = activeState === 'done';
  const isError = activeState === 'error';
  const errorMsg = mode === 'chunked' ? chunked.errorMsg : shortMic.errorMsg;
  const disabled = isProcessing;

  // Reset mode when done/error clears back to idle
  if (mode && activeState === 'idle') {
    setTimeout(() => setMode(null), 0);
  }

  const handlePointerDown = useCallback((e) => {
    e.stopPropagation(); // prevent parent elements from receiving this event
    if (isRecording || isProcessing || disabled) return;

    longPressTriggeredRef.current = false;
    pressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      setMode('chunked');
      chunked.toggle();
    }, LONG_PRESS_MS);
  }, [isRecording, isProcessing, disabled, chunked]);

  const handlePointerUp = useCallback((e) => {
    e.stopPropagation();

    // Clear long-press timer
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }

    if (disabled) return;

    // If long-press just triggered chunked mode, do nothing on pointer up
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return;
    }

    // STOP recording
    if (isRecording) {
      if (modeRef.current === 'chunked') {
        chunked.toggle();
      } else {
        shortMic.toggle();
      }
      return;
    }

    // START short recording
    if (!isRecording && !isProcessing) {
      setMode('short');
      shortMic.toggle();
    }
  }, [isRecording, isProcessing, disabled, chunked, shortMic]);

  const handlePointerLeave = useCallback(() => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  }, []);

  const cycleProfile = useCallback(() => {
    if (!profiles || profiles.length < 2) return;
    const currentIdx = profiles.indexOf(selectedProfile || profiles[0]);
    const nextIdx = (currentIdx + 1) % profiles.length;
    setSelectedProfile(profiles[nextIdx]);
  }, [profiles, selectedProfile]);

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
      {profiles && profiles.length > 1 && !isRecording && !isProcessing && (
        <button
          data-testid="mic-profile-toggle"
          onClick={(e) => { e.stopPropagation(); cycleProfile(); }}
          title={`Recording mode: ${PROFILE_LABELS[selectedProfile || profiles[0]] || 'Note'}. Tap to switch.`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            padding: '3px 8px', borderRadius: 10,
            fontSize: 10, fontWeight: 600, letterSpacing: '0.02em',
            border: 'none', cursor: 'pointer',
            background: (selectedProfile === 'survey') ? 'rgba(14,165,233,0.12)' : 'var(--stodob)',
            color: (selectedProfile === 'survey') ? 'var(--info)' : 'var(--tm)',
            transition: 'all 150ms',
            minHeight: 24,
          }}
        >
          {(selectedProfile === 'survey') && <Compass size={10} />}
          {PROFILE_LABELS[selectedProfile || profiles[0]] || 'Note'}
        </button>
      )}
      <button
        data-testid="entity-mic"
        data-voice-state={activeState}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerLeave}
        disabled={disabled}
        title={isRecording
          ? 'Tap to stop recording'
          : 'Tap: quick note · Hold: long recording'}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 36, height: 36, borderRadius: '50%',
          background: isRecording
            ? mode === 'chunked' ? 'rgba(168,85,247,0.15)' : 'rgba(239,68,68,0.15)'
            : 'transparent',
          border: 'none', cursor: disabled ? 'default' : 'pointer',
          transition: 'background 150ms',
          padding: 0,
          animation: isRecording
            ? mode === 'chunked' ? 'entityMicPulseChunked 1.2s ease infinite' : 'entityMicPulse 1.2s ease infinite'
            : 'none',
          opacity: disabled ? 0.5 : 1,
          userSelect: 'none',
          touchAction: 'none',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {isRecording ? (
          // HEX_TODO[#A855F7] exception — see color-map.json
          <Square size={16} fill={mode === 'chunked' ? '#A855F7' : 'var(--bad)'} color={mode === 'chunked' ? '#A855F7' : 'var(--bad)'} />
        ) : isProcessing ? (
          <Loader size={16} color="var(--accent)" style={{ animation: 'spin 1s linear infinite' }} />
        ) : (
          <Mic size={16} color={isDone ? 'var(--ok)' : isError ? 'var(--bad)' : 'var(--tm)'} />
        )}
      </button>
      {isRecording && mode === 'chunked' && (
        // HEX_TODO[#A855F7] exception — see color-map.json
        <span style={{ fontSize: 11, color: '#A855F7', fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          {formatElapsed(chunked.elapsed)}
          {chunked.chunkCount > 0 && ` · ${chunked.chunkCount} chunks`}
        </span>
      )}
      {isRecording && mode === 'short' && (
        <span style={{ fontSize: 11, color: 'var(--bad)', fontWeight: 600, whiteSpace: 'nowrap' }}>Recording...</span>
      )}
      {isProcessing && (
        <span style={{ fontSize: 11, color: 'var(--tm)', whiteSpace: 'nowrap' }}>Processing...</span>
      )}
      {isDone && (
        <span style={{ fontSize: 11, color: 'var(--ok)', fontWeight: 600, whiteSpace: 'nowrap' }}>Saved</span>
      )}
      {isError && (
        <span style={{ fontSize: 11, color: 'var(--bad)', whiteSpace: 'nowrap' }}>{errorMsg || 'Error'}</span>
      )}
      <style>{`
        @keyframes entityMicPulse {
          0%, 100% { background: rgba(239,68,68,0.08); }
          50% { background: rgba(239,68,68,0.20); }
        }
        @keyframes entityMicPulseChunked {
          0%, 100% { background: rgba(168,85,247,0.08); }
          50% { background: rgba(168,85,247,0.20); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </span>
  );
}
